import { ChangeEvent, KeyboardEvent, ReactElement, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useSearchAnalytics } from '@client/analytics/context/search/SearchAnalyticsContext';
import { useFetchSuggestionsAction, useSuggestionsData } from '@client/catalog/hooks';
import { FILTERS } from '@client/catalog/utils/facets';
import { useDeviceType } from '@client/common/hooks';
import { isMobile } from '@client/common/utils/env';
import { useTranslate } from '@client/i18n/hooks';
import { ROUTES } from '@client/routes/components/Router/routes';
import { useRouterPush, useUrlCreator } from '@client/routes/hooks';
import { getSearchUrlParams } from '@client/routes/utils/url';
import { SearchField as SearchFieldComponent } from 'b2b-components/theme/components/Header/SearchField';
import { Props } from './SearchField.types';

const MIN_SUGGESTIONS_LENGTH = 2;

export const SearchField = ({
  searchRef,
  onOpenAutosuggest,
  onCloseAutosuggest,
  onInputKeyDown,
  className,
  closeButtonDataAttributes,
  submitButtonDataAttributes,
}: Props): ReactElement => {
  const t = useTranslate();
  const { pathname, search } = useLocation();
  const query = new URLSearchParams(search).get(FILTERS.query) || '';
  const [searchQuery, setSearchQuery] = useState(query);
  const previousQueryRef = useRef<string>();
  const push = useRouterPush();
  const urlCreator = useUrlCreator();
  const deviceType = useDeviceType();
  const fetchSuggestions = useFetchSuggestionsAction();
  const suggestions = useSuggestionsData();
  const { analyzeSearchEvent } = useSearchAnalytics();

  useEffect(
    (): void => {
      if (query !== previousQueryRef.current) {
        setSearchQuery(query);
        previousQueryRef.current = query;
      }
    },
    [pathname, query],
  );

  const handleSubmit = (): void => {
    if (searchQuery.length > 0) {
      push(
        `${urlCreator(ROUTES.search)}${getSearchUrlParams(
          searchQuery.trim(),
        )}`,
      );
      analyzeSearchEvent();
    }
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const newQuery = event.currentTarget.value;
    setSearchQuery(newQuery);

    if (newQuery && newQuery.length >= MIN_SUGGESTIONS_LENGTH) {
      onOpenAutosuggest();
      fetchSuggestions(newQuery.trim());
    } else {
      onCloseAutosuggest();
    }
  };

  const handleInputKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Escape' || event.key === 'Esc') {
      onCloseAutosuggest();
    }

    onInputKeyDown(event);
  };

  const handleClearButtonClick = (): void => {
    setSearchQuery('');
    onCloseAutosuggest();
  };

  return (
    <SearchFieldComponent
      ref={searchRef}
      value={searchQuery}
      onInputChange={handleInputChange}
      onInputKeyDown={handleInputKeyDown}
      onSubmit={handleSubmit}
      isLoading={suggestions.wasStarted && suggestions.isLoading}
      deviceType={deviceType}
      placeholder={isMobile(deviceType)
        ? t('Search')
        : t('Search by name or item number')
      }
      dataAttributes={{ 'data-testid': 'headerSearchField' }}
      onClearButtonClick={handleClearButtonClick}
      className={className}
      submitButtonDataAttributes={submitButtonDataAttributes}
      closeButtonDataAttributes={closeButtonDataAttributes}
    />
  );
};
